import { gql } from '@apollo/client';

// Stages are display groupings of tasks (not a sequential pipeline). Stages 1-4
// organize the checklist; overall progress is tracked by `status` below.
export const PartnerOnboardingStages = {
  1: { key: 1, label: 'ITSM Follow Up', color: 'blue' },
  2: { key: 2, label: 'Regional Cert', color: 'orange' },
  3: { key: 3, label: 'HQ Mobilization', color: 'purple' },
  4: { key: 4, label: 'HQ Validation', color: 'cyan' },
};

export const ONBOARDING_STAGE_KEYS = [1, 2, 3, 4];

export const PartnerOnboardingStatuses = {
  IN_PROGRESS: { key: 'IN_PROGRESS', label: 'In Progress', color: 'blue' },
  COMPLETE: { key: 'COMPLETE', label: 'Complete', color: 'green' },
  CANCELLED: { key: 'CANCELLED', label: 'Cancelled', color: 'red' },
};

export const TaskInputTypes = {
  YES_NO: { key: 'YES_NO', label: 'Yes/No' },
  DATE: { key: 'DATE', label: 'Date' },
  TEXT: { key: 'TEXT', label: 'Text' },
  FORM: { key: 'FORM', label: 'Form' },
};

export const OnboardingTaskRoles = {
  RMC_A: { key: 'RMC_A', label: 'RMC-A' },
  RMC_M: { key: 'RMC_M', label: 'RMC-M' },
  RMD: { key: 'RMD', label: 'RMD' },
  GMM: { key: 'GMM', label: 'GMM' },
  GMD: { key: 'GMD', label: 'GMD' },
  BOT: { key: 'BOT', label: 'Bot' },
  RMC_STAFF: { key: 'RMC_STAFF', label: 'AIMS RMC - Staff Member' },
  HQ_STAFF: { key: 'HQ_STAFF', label: 'Staff Adoption Admin' },
};

// When set, the system auto-completes this task once the matching signal is true
// on the partner's contact (no manual check-off needed).
export const OnboardingTaskAutoSources = {
  CONTACT_EMAIL_AND_PHONE: {
    key: 'CONTACT_EMAIL_AND_PHONE',
    label: 'Auto: Email & phone entered',
  },
  STRATEGY_GUIDE_UPLOADED: {
    key: 'STRATEGY_GUIDE_UPLOADED',
    label: 'Auto: Strategy Guide uploaded',
  },
  STRATEGY_GUIDE_APPROVED: {
    key: 'STRATEGY_GUIDE_APPROVED',
    label: 'Auto: Strategy Guide approved',
  },
  COMMITMENT_FORM_CREATED: {
    key: 'COMMITMENT_FORM_CREATED',
    label: 'Auto: Commitment Form created',
  },
  COMMITMENT_FORM_SENT: {
    key: 'COMMITMENT_FORM_SENT',
    label: 'Auto: Commitment Form sent for signatures',
  },
  COMMITMENT_FORM_SIGNED: {
    key: 'COMMITMENT_FORM_SIGNED',
    label: 'Auto: Commitment Form (MOU) signed',
  },
  USER_INVITE_SENT: {
    key: 'USER_INVITE_SENT',
    label: 'Auto: User invite sent',
  },
  USER_ACCOUNT_CREATED: {
    key: 'USER_ACCOUNT_CREATED',
    label: 'Auto: User account created',
  },
  COORDINATOR_COMMITMENT_FORM_SIGNED: {
    key: 'COORDINATOR_COMMITMENT_FORM_SIGNED',
    label: 'Auto: Coordinator Partnership Commitment Form signed',
  },
  TRAINER_ASSESSMENT_PASSED: {
    key: 'TRAINER_ASSESSMENT_PASSED',
    label: 'Auto: Trainer assessment passed',
  },
};

export const partnerOnboardingListAttributes = gql`
  {
    _id
    contactId
    contact {
      _id
      fullName
    }
    canViewContact
    coordinatorContactId
    coordinatorContact {
      _id
      fullName
    }
    canViewCoordinatorContact
    partnerType
    region
    regionName
    status
    onboardingCompletedDate
    tasksDoneCount
    tasksTotalCount
    assignedStaffIds
    notes
    createdBy
    updatedBy
    createdAt
    updatedAt
  }
`;

export const partnerOnboardingDetailedAttributes = gql`
  {
    _id
    contactId
    contact {
      _id
      fullName
    }
    partnerType
    region
    regionName
    canViewContact
    coordinatorContactId
    coordinatorContact {
      _id
      fullName
    }
    canViewCoordinatorContact
    status
    onboardingCompletedDate
    reopenHistory {
      reopenedAt
      reopenedBy
      reason
    }
    cancellationReason
    onboardingCancelledDate
    cancelledBy
    taskResponses {
      templateId
      stageNumber
      answer
      completedAt
      completedBy
      notes
    }
    tasksDoneCount
    tasksTotalCount
    assignedStaffIds
    assignedStaff {
      _id
      name
    }
    notes
    createdBy
    updatedBy
    createdAt
    updatedAt
    deletedBy
    deletedAt
  }
`;

export const allPartnerOnboardingsQuery = gql`
  query AllPartnerOnboardings(
    $first: Int
    $after: String
    $filters: PartnerOnboardingFilters
    $sortBy: [SortBy]
  ) {
    allPartnerOnboardings(
      first: $first
      after: $after
      filters: $filters
      sortBy: $sortBy
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node ${partnerOnboardingListAttributes}
      }
    }
  }
`;

// All onboardings a contact has been through (newest first) — full detail for each.
export const partnerOnboardingsByContactQuery = gql`
  query PartnerOnboardingsByContact($contactId: ID!) {
    allPartnerOnboardings(first: 100, filters: { contactId: $contactId }) {
      edges {
        node ${partnerOnboardingDetailedAttributes}
      }
    }
  }
`;

export const extractPartnerOnboardingsByContact = (data) =>
  (data?.allPartnerOnboardings?.edges || [])
    .map((e) => e.node)
    .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

// Lightweight status-only lookup for the contact's onboarding rounds — used to
// badge the "Partner Onboarding" tab label without loading full task detail.
export const contactOnboardingStatusesQuery = gql`
  query ContactOnboardingStatuses($contactId: ID!) {
    allPartnerOnboardings(first: 100, filters: { contactId: $contactId }) {
      edges {
        node {
          _id
          status
          updatedAt
        }
      }
    }
  }
`;

// The status that represents the contact overall: an active (in-progress) round
// if there is one, otherwise the most recently updated round.
export const extractContactOnboardingStatus = (data) => {
  const nodes = (data?.allPartnerOnboardings?.edges || []).map((e) => e.node);
  if (!nodes.length) return null;
  const active = nodes.find((n) => n.status === 'IN_PROGRESS');
  if (active) return active.status;
  const latest = [...nodes].sort(
    (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
  )[0];
  return latest?.status || null;
};

export const extractAllPartnerOnboardings = (data) =>
  data?.allPartnerOnboardings;

export const partnerOnboardingQuery = gql`
  query PartnerOnboarding($_id: ID!) {
    partnerOnboarding(_id: $_id) ${partnerOnboardingDetailedAttributes}
  }
`;

export const extractPartnerOnboarding = (data) => data?.partnerOnboarding;

export const createPartnerOnboardingMutation = gql`
  mutation CreatePartnerOnboarding(
    $partnerOnboarding: PartnerOnboardingCreateInput!
  ) {
    createPartnerOnboarding(partnerOnboarding: $partnerOnboarding) {
      partnerOnboarding ${partnerOnboardingDetailedAttributes}
    }
  }
`;

export const updatePartnerOnboardingMutation = gql`
  mutation UpdatePartnerOnboarding(
    $partnerOnboarding: PartnerOnboardingUpdateInput!
  ) {
    updatePartnerOnboarding(partnerOnboarding: $partnerOnboarding) {
      partnerOnboarding ${partnerOnboardingDetailedAttributes}
    }
  }
`;

export const deletePartnerOnboardingMutation = gql`
  mutation DeletePartnerOnboarding($_id: ID!) {
    deletePartnerOnboarding(_id: $_id) {
      deletedId
    }
  }
`;

export const updateOnboardingTaskNotesMutation = gql`
  mutation UpdateOnboardingTaskNotes($input: UpdateOnboardingTaskNotesInput!) {
    updateOnboardingTaskNotes(input: $input) {
      partnerOnboarding ${partnerOnboardingDetailedAttributes}
    }
  }
`;

export const submitOnboardingTaskResponseMutation = gql`
  mutation SubmitOnboardingTaskResponse(
    $input: SubmitOnboardingTaskResponseInput!
  ) {
    submitOnboardingTaskResponse(input: $input) {
      partnerOnboarding ${partnerOnboardingDetailedAttributes}
    }
  }
`;

// Paged to exhaustion by `useEligibleCoordinators` — a single fixed page silently drops
// coordinators once an environment holds more than one page of them.
export const eligibleCoordinatorsQuery = gql`
  query EligibleCoordinators($after: String) {
    allPartnerOnboardings(
      first: 200
      after: $after
      filters: { partnerType: "C" }
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          _id
          contactId
          status
          contact {
            _id
            fullName
          }
        }
      }
    }
  }
`;

export const extractEligibleCoordinators = (data) =>
  (data?.allPartnerOnboardings?.edges || [])
    .map((e) => e.node)
    .filter((n) => n.contactId);

export const regionalCoordContactsQuery = gql`
  query RegionalCoordContacts($after: String) {
    allContactsForAdmin(
      first: 200
      after: $after
      filters: { isRegionalCoord: true }
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          _id
          fullName
        }
      }
    }
  }
`;

export const extractRegionalCoordContacts = (data) =>
  (data?.allContactsForAdmin?.edges || []).map((e) => e.node).filter(Boolean);

export const createTrainerAssessmentMutation = gql`
  mutation CreateTrainerAssessment($assessment: TrainerAssessmentCreateInput!) {
    createTrainerAssessment(assessment: $assessment) {
      _id
      certified
    }
  }
`;

export const trainerAssessmentsForContactQuery = gql`
  query TrainerAssessmentsForContact($trainerPartnerId: ID) {
    allTrainerAssessments(trainerPartnerId: $trainerPartnerId) {
      _id
      trainerPartnerId
      assessorEmail
      timestamp
      itsmFoundationsModules
      itsmToolsModules
      knowledgeScore
      questionsScore
      mobilizeScore
      improvementNotes
      certified
      reassessmentDate
    }
  }
`;

export const reopenPartnerOnboardingMutation = gql`
  mutation ReopenPartnerOnboarding($input: ReopenPartnerOnboardingInput!) {
    reopenPartnerOnboarding(input: $input) {
      partnerOnboarding ${partnerOnboardingDetailedAttributes}
    }
  }
`;

export const cancelPartnerOnboardingMutation = gql`
  mutation CancelPartnerOnboarding($input: CancelPartnerOnboardingInput!) {
    cancelPartnerOnboarding(input: $input) {
      partnerOnboarding ${partnerOnboardingDetailedAttributes}
    }
  }
`;

export const onboardingTaskTemplateListAttributes = gql`
  {
    _id
    taskTypeId
    stageNumber
    taskName
    inputType
    assignedRole
    daysToComplete
    partnerTypeCodes
    isActive
    order
    autoSource
    allowNotApplicable
  }
`;

export const allOnboardingTaskTemplatesQuery = gql`
  query AllOnboardingTaskTemplates(
    $first: Int
    $after: String
    $filters: OnboardingTaskTemplateFilters
    $sortBy: [SortBy]
  ) {
    allOnboardingTaskTemplates(
      first: $first
      after: $after
      filters: $filters
      sortBy: $sortBy
    ) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node ${onboardingTaskTemplateListAttributes}
      }
    }
  }
`;

export const extractAllOnboardingTaskTemplates = (data) =>
  data?.allOnboardingTaskTemplates;

export const createOnboardingTaskTemplateMutation = gql`
  mutation CreateOnboardingTaskTemplate(
    $template: OnboardingTaskTemplateCreateInput!
  ) {
    createOnboardingTaskTemplate(template: $template) {
      template ${onboardingTaskTemplateListAttributes}
    }
  }
`;

export const updateOnboardingTaskTemplateMutation = gql`
  mutation UpdateOnboardingTaskTemplate(
    $template: OnboardingTaskTemplateUpdateInput!
  ) {
    updateOnboardingTaskTemplate(template: $template) {
      template ${onboardingTaskTemplateListAttributes}
    }
  }
`;

export const deleteOnboardingTaskTemplateMutation = gql`
  mutation DeleteOnboardingTaskTemplate($_id: ID!) {
    deleteOnboardingTaskTemplate(_id: $_id) {
      deletedId
    }
  }
`;

// The Home widget renders only the three summary fields; selecting the full document
// there would make the server resolve (and discard) every stalled row's contact name and
// `canViewContact` permission check on each dashboard load.
export const partnerOnboardingStatsSummaryQuery = gql`
  query PartnerOnboardingStatsSummary {
    partnerOnboardingStats {
      statusCounts {
        status
        count
      }
      stageTaskCounts {
        stage
        completed
        total
      }
      quarterFlowCounts {
        quarter
        started
        completed
      }
    }
  }
`;

export const partnerOnboardingStatsQuery = gql`
  query PartnerOnboardingStats {
    partnerOnboardingStats {
      truncated
      statusCounts {
        status
        count
      }
      stageTaskCounts {
        stage
        completed
        total
      }
      regionFlowCounts {
        region
        regionName
        started
        completed
      }
      partnerTypeCounts {
        partnerType
        count
      }
      bottlenecks {
        stalledCount
        overdueTaskCount
        oldestInProgressDays
        stalledAfterDays
        stalled {
          partnerOnboardingId
          contactId
          contactName
          canViewContact
          partnerType
          region
          regionName
          daysIdle
          openTasks
        }
      }
      pendingTasksByStageRole {
        stage
        role
        count
      }
      pendingTasksByPerson {
        userId
        userName
        count
      }
      completedTasksByPersonMonth {
        userId
        userName
        month
        count
      }
      quarterFlowCounts {
        quarter
        started
        completed
      }
    }
  }
`;

export const extractPartnerOnboardingStats = (data) =>
  data?.partnerOnboardingStats;

export const runOnboardingAutoChecksMutation = gql`
  mutation RunOnboardingAutoChecks($_id: ID!) {
    runOnboardingAutoChecks(_id: $_id) {
      partnerOnboarding ${partnerOnboardingDetailedAttributes}
    }
  }
`;

export const myOnboardingTasksQuery = gql`
  query MyOnboardingTasks {
    myOnboardingTasks {
      partnerOnboardingId
      contactId
      contactName
      region
      regionName
      partnerType
      templateId
      taskName
      stageNumber
      inputType
      daysToComplete
      allowNotApplicable
    }
  }
`;

export const extractMyOnboardingTasks = (data) => data?.myOnboardingTasks ?? [];

export const allPartnerTypesQuery = gql`
  query AllPartnerTypes {
    allPartnerTypes {
      _id
      label
      order
    }
  }
`;

export const extractAllPartnerTypes = (data) => data?.allPartnerTypes ?? [];

// Partner type code -> full name (mirrors cloud/api/scripts/seed_partner_types.py).
export const PartnerTypes = {
  C: 'Coordinator',
  F: 'Field Worker',
  T: 'Trainer',
  PN: 'Prayer Networkers',
  A: 'UPG Adopters and Advocates',
  S: 'Field Worker Sponsors',
  PM: 'Partner Ministries',
  STT: 'Short Term Cross Cultural Missionaries',
  LTT: 'Long Term Cross Cultural Missionaries',
  HPM: 'Holistic Project Managers',
  HPP: 'Holistic Project Funding Partners',
  PC: 'Partner Churches',
  // 'L' (Lydia) removed 2026-07 — merged into Holistic Project Managers (HPM).
};

export const partnerTypeLabel = (code) =>
  code ? PartnerTypes[code] || code : '';
